import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { IDTO } from '../domain/i-dto.model';

const API_URL = environment.webApiUrl;
const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
};

@Injectable({
  providedIn: 'root',
})
export class BaseDataService<TData extends IDTO> {

  protected baseUrl: string;

  constructor(protected http: HttpClient) {
    this.baseUrl = API_URL;
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * The throwError could be catch by the global error handler, if not handled in the caller.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  protected handleError<TError>(operation = 'operation', result?: TError) {
    return (error: any): Observable<TError> => {

      // TODO: send the error to remote logging infrastructure
      console.error('BaseDataService handleError...:', error); // log to console instead

      // Let the app keep running by returning an empty result.
      // return of(result as TError);

      // rewrite unknown error message for site disabled
      if (error.statusText === 'Unknown Error' && error.message === 'Http failure response for (unknown url): 0 Unknown Error') {
        return throwError(new HttpErrorResponse({ error, status: 500, statusText: 'The api server is disabled', url: this.baseUrl }));
      }
      return throwError(error);
    };
  }

  // GET: /api/entity/{id}
  get(requestUrl: string, id: string | number, options: string = ''): Observable<TData> {
    const url = `${this.baseUrl}/${requestUrl}/${id}${options}`;
    return this.http.get<TData>(url)
      .pipe(
        catchError(this.handleError<TData>(`get id=${id}`)),
      );
  }

  downloadPdf(requestUrl: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/${requestUrl}`, { observe: 'response', responseType: 'blob' })
      .pipe(
        map(res => {
          // the Cors setting on api must expose this header
          const contentDisposition = res.headers.get('content-disposition') || '';
          const matches = /filename=([^;]+)/ig.exec(contentDisposition);
          const fileName = (matches[1] || 'untitled').trim().replace(/['"]+/g, '');

          const data = {
            pdf: new Blob([res.body], { type: res.headers.get('Content-Type') }),
            filename: fileName,
          };

          return data;
        }),
      );
  }

  // GET: /api/entity
  list(requestUrl: string, params?: HttpParams): Observable<TData[]> {
    const url = `${this.baseUrl}/${requestUrl}`;
    return this.http.get<TData[]>(url, { params })
      .pipe(
        catchError(this.handleError('list', [])),
      );
  }

  // POST: /api/entity
  post(requestUrl: string, dto: TData): Observable<TData> {
    const url = `${this.baseUrl}/${requestUrl}`;
    return this.http.post<TData>(url, dto, httpOptions).pipe(
      catchError(this.handleError<TData>('post')),
    );
  }

  put(requestUrl: string, dto: TData): Observable<TData> {
    const url = `${this.baseUrl}/${requestUrl}`;
    return this.http.put<TData>(url, dto, httpOptions).pipe(
      catchError(this.handleError<TData>('put')),
    );
  }
}
